// Which fix does this issue offer, and with what words?
//
// Pure and root-safe (imports only the browser-safe shared/cleanup/fix
// module), so the node pool can test it without booting an isolate — the
// same convention as decide-rail-applicability.ts and
// record-move-predicate.ts.
//
// THE RULE THIS REPLACES. Until 2026-09-15 the Cleanup pane decided what
// button to show from hand-written lists of detector slugs and verbs, one
// case per detector. An issue whose detector was not on a list fell
// through to dispositions that change nothing on the record, presented as
// if they were the answer (~600 of ~1,065 open issues that morning).
//
// Now the DETECTOR declares the fix (issue.fix, persisted as fix_json) and
// this module turns that declaration into labels the card can render.
// A declaration wins outright. For issues materialized before their
// detector declared anything — a sweep refreshes fix_json within ~6h, so
// this is a bridge, not a second mechanism — a narrow inference stands in:
// a `move-household` recommendation, or exactly one movable record on a
// detector known to ask "which household?" (PLACEMENT_SLUGS). The
// inference is deliberately conservative: a won opportunity with no
// product record is ALSO "one record + one household" and must never grow
// a move button, so shape alone is never enough.

import {
  isMovableKind,
  parseCleanupFix,
  type CleanupAskedField,
  type CleanupChooseAnswer,
  type CleanupFieldWriter,
  type CleanupFieldsAlternative,
  type CleanupOpenPicker,
  type CleanupFix,
  type CleanupFixConfidence,
  type CleanupPickLink,
  type CleanupFillEscape,
  type CleanupFillInput,
  type CleanupPickOption,
  type CleanupSplitAccount,
  type MovableKind,
  type TypeItInRecordKind,
} from "../../../shared/cleanup/fix";

export type IssueRef = {
  kind: string;
  id: string;
  label: string;
  members_label?: string | null;
  record_gone?: boolean;
};

export interface FixIssueLike {
  detectorSlug?: string;
  entityKind: string;
  affectedRecords: IssueRef[];
  recommendation?: {
    verb: string;
    label?: string;
    params?: Record<string, unknown> | null;
  } | null;
  fix?: CleanupFix | null;
  confidence?: number;
  owningCompanySlug?: string | null;
}

export interface ResolvedMove {
  kind: "move-record";
  title: string;
  record: IssueRef & { kind: MovableKind };
  /** Where it sits now, with its label; null when the issue never named it. */
  from: IssueRef | null;
  /** Where it belongs, when the detector could say; null opens on search. */
  to: IssueRef | null;
  confidence: CleanupFixConfidence;
  /** True when the detector declared this; false when inferred (bridge). */
  declared: boolean;
}

export interface ResolvedOpenPage {
  kind: "open-page";
  title: string;
  what: string;
  link: { label: string; path: string };
  declared: true;
}

export interface ResolvedAcknowledge {
  kind: "acknowledge";
  title: string;
  declared: true;
}

export interface ResolvedKeepOne {
  kind: "keep-one";
  title: string;
  /** The cluster, with the labels the issue carries, in detector order. */
  candidates: (IssueRef & { kind: MovableKind })[];
  /** Pre-selected in the card; null means the person picks from scratch. */
  keeperId: string | null;
  /** "They're all real" — keeps every record, closes with a reason. */
  keepAll?: { label: string; consequence: string };
  retire: "archive" | "merge";
  confidence: CleanupFixConfidence;
  declared: true;
}

export interface ResolvedTypeItIn {
  kind: "type-it-in";
  title: string;
  editor: "deal-numbers" | "record-people";
  /** The record being edited, with the label the issue carries. */
  record: IssueRef & { kind: TypeItInRecordKind };
  /** Null only for deal-numbers, which does not need one. */
  householdId: string | null;
  confidence: CleanupFixConfidence;
  declared: true;
}

/** The detector named its own fields (fix.ts, editor "fields"): the card
 *  renders them and saves through the named writer. Its own resolved kind
 *  so the card dispatch and the row icon can tell it from the two
 *  record-editor flavours without inspecting `editor`. */
export interface ResolvedTypeItInFields {
  kind: "type-it-in-fields";
  title: string;
  /** The record the issue is about, with the label the issue carries. */
  record: IssueRef;
  householdId: string | null;
  confidence: CleanupFixConfidence;
  writer: CleanupFieldWriter;
  writerId: string;
  fields: readonly CleanupAskedField[];
  alternatives: readonly CleanupFieldsAlternative[];
  closeOnSave: boolean;
  declared: true;
}

/** A decision about something outside the OS (fix.ts, "pick-one"): the
 *  card offers the declared answers and records which was chosen. */
export interface ResolvedPickOne {
  kind: "pick-one";
  title: string;
  options: readonly CleanupPickOption[];
  link: CleanupPickLink | null;
  confidence: CleanupFixConfidence;
  declared: true;
}

/** A carrier policy that never landed in the OS (fix.ts, "add-policy"):
 *  the card finds the household and opens the Add Policy form pre-filled. */
export interface ResolvedAddPolicy {
  kind: "add-policy";
  title: string;
  prefill: { carrier: string | null; policyNumber: string | null; insuredName: string | null };
  searchHint: string | null;
  confidence: CleanupFixConfidence;
  declared: true;
}

/** One value answered on the card (fix.ts, "fill-one"). */
export interface ResolvedFillOne {
  kind: "fill-one";
  title: string;
  input: CleanupFillInput;
  applyLabel: string;
  blankApplyLabel: string | null;
  escapes: readonly CleanupFillEscape[];
  householdId: string | null;
  verb: "verify" | "match";
  confidence: CleanupFixConfidence;
  declared: true;
}

/** Contacts that might be one person (fix.ts, "same-person"). The
 *  people are the issue's own contact refs, in order; a merged-away or
 *  archived one is dropped, and fewer than two left means nothing to ask. */
export interface ResolvedSamePerson {
  kind: "same-person";
  title: string;
  people: readonly IssueRef[];
  matchType: "email" | "phone" | "name_zip";
  matchValue: string | null;
  likelySame: boolean;
  confidence: CleanupFixConfidence;
  declared: true;
}

/** A question with two to four real answers (fix.ts, "choose-answer").
 *  The answers come through as declared, the recommended one first. */
export interface ResolvedChooseAnswer {
  kind: "choose-answer";
  title: string;
  answers: readonly CleanupChooseAnswer[];
  confidence: CleanupFixConfidence;
  declared: true;
}

/** Households that might be one family (fix.ts, "same-household"). The
 *  households are the issue's own household refs; a merged-away or
 *  archived one is dropped, and fewer than two left means nothing to ask.
 *  A survivor that is not one of them is dropped, never guessed. */
export interface ResolvedSameHousehold {
  kind: "same-household";
  title: string;
  variant?: "import-stub";
  households: readonly IssueRef[];
  survivorId: string | null;
  likelySame: boolean;
  confidence: CleanupFixConfidence;
  declared: true;
}

/** A placeholder household to find the family for (fix.ts,
 *  "find-household"). Exactly one live household ref, or nothing to ask. */
export interface ResolvedFindHousehold {
  kind: "find-household";
  title: string;
  household: IssueRef;
  confidence: CleanupFixConfidence;
  declared: true;
}

/** A document past retention (fix.ts, "retention"). */
export interface ResolvedRetention {
  kind: "retention";
  title: string;
  confidence: CleanupFixConfidence;
  declared: true;
}

/** An existing picker opened from the card (fix.ts, "open-picker"). */
export interface ResolvedOpenPicker {
  kind: "open-picker";
  title: string;
  picker: CleanupOpenPicker;
  label: string;
  consequence: string;
  confidence: CleanupFixConfidence;
  declared: true;
}

/** A win nothing was created from (fix.ts, "rewin"). */
export interface ResolvedRewin {
  kind: "rewin";
  title: string;
  rows: { id: string; label: string; opportunityType: string }[];
  companySlug: string;
  householdName: string;
  confidence: CleanupFixConfidence;
  declared: true;
}

/** A record missing required documents (fix.ts, "missing-documents"). */
export interface ResolvedMissingDocuments {
  kind: "missing-documents";
  title: string;
  record: IssueRef;
  confidence: CleanupFixConfidence;
  declared: true;
}

/** A holding household whose accounts each need their own home (fix.ts,
 *  "split-accounts"). The holding household must be the issue's one live
 *  household ref; otherwise there is nothing to split from. */
export interface ResolvedSplitAccounts {
  kind: "split-accounts";
  title: string;
  holding: IssueRef;
  accounts: readonly CleanupSplitAccount[];
  /** Accounts waiting beyond the shown batch (the card says how many). */
  moreAccounts?: number;
  confidence: CleanupFixConfidence;
  declared: true;
}

export type ResolvedFix =
  | ResolvedSplitAccounts
  | ResolvedOpenPicker
  | ResolvedRewin
  | ResolvedMissingDocuments
  | ResolvedRetention
  | ResolvedFindHousehold
  | ResolvedSameHousehold
  | ResolvedChooseAnswer
  | ResolvedSamePerson
  | ResolvedFillOne
  | ResolvedAddPolicy
  | ResolvedPickOne
  | ResolvedMove
  | ResolvedAcknowledge
  | ResolvedOpenPage
  | ResolvedKeepOne
  | ResolvedTypeItIn
  | ResolvedTypeItInFields;

/**
 * Detectors whose whole question is "which household does this record
 * belong to?" — the bridge for issues stored before their detector
 * declared a fix. Every slug here now declares one; the list exists so
 * the open rows work TODAY rather than after the next sweep. Do not add
 * to it: a new detector declares its fix instead.
 */
const PLACEMENT_SLUGS: readonly string[] = [
  "mortgage-loan-wrong-household",
  "portfolio-wrong-household",
  "record-on-archived-household",
  "cross-household-link-review",
  "financial-import-stub-review",
];

const PLACEMENT_TITLES: Record<string, string> = {
  "mortgage-loan-wrong-household": "Loan on the wrong household",
  "portfolio-wrong-household": "Account on the wrong household",
  "record-on-archived-household": "Record on an archived household",
  "cross-household-link-review": "Record on the wrong household",
  "financial-import-stub-review": "Account with no household",
};

/** Two independent facts is what "confident" means on every declaring
 *  detector; a recommendation alone can be a name-only match at 65. */
const CONFIDENT_FLOOR = 85;

function householdRefs(issue: FixIssueLike): IssueRef[] {
  return issue.affectedRecords.filter((r) => r.kind === "household");
}

function refById(issue: FixIssueLike, id: string | null): IssueRef | null {
  if (!id) return null;
  return issue.affectedRecords.find((r) => r.id === id) ?? null;
}

function fromDeclared(issue: FixIssueLike, fix: CleanupFix): ResolvedFix | null {
  if (fix.kind === "acknowledge") {
    return { kind: "acknowledge", title: fix.title, declared: true };
  }
  if (fix.kind === "open-page") {
    return { kind: "open-page", title: fix.title, what: fix.what, link: fix.link, declared: true };
  }
  if (fix.kind === "open-picker") {
    return {
      kind: "open-picker",
      title: fix.title,
      picker: fix.picker,
      label: fix.label,
      consequence: fix.consequence,
      confidence: "needs-you",
      declared: true,
    };
  }
  if (fix.kind === "rewin") {
    if (!issue.owningCompanySlug) return null;
    const rows = fix.opportunities
      .map((o) => {
        const ref = issue.affectedRecords.find((r) => r.kind === "opportunity" && r.id === o.id);
        return ref && !ref.record_gone ? { id: o.id, label: ref.label, opportunityType: o.opportunityType } : null;
      })
      .filter((r): r is { id: string; label: string; opportunityType: string } => r !== null);
    if (rows.length === 0) return null;
    return {
      kind: "rewin",
      title: fix.title,
      rows,
      companySlug: issue.owningCompanySlug,
      householdName: issue.affectedRecords.find((r) => r.kind === "household")?.label ?? "this household",
      confidence: "needs-you",
      declared: true,
    };
  }
  if (fix.kind === "split-accounts") {
    const households = issue.affectedRecords.filter((r) => r.kind === "household");
    const holding = households.length === 1 ? households[0]! : null;
    if (!holding || holding.record_gone || holding.id !== fix.holdingHouseholdId) return null;
    return {
      kind: "split-accounts",
      ...(fix.moreAccounts ? { moreAccounts: fix.moreAccounts } : {}),
      title: fix.title,
      holding,
      accounts: fix.accounts,
      confidence: fix.confidence,
      declared: true,
    };
  }
  if (fix.kind === "missing-documents") {
    const record = issue.affectedRecords.find((r) => r.kind === "service_record" && !r.record_gone);
    if (!record) return null;
    return { kind: "missing-documents", title: fix.title, record, confidence: "needs-you", declared: true };
  }
  if (fix.kind === "retention") {
    if (!issue.affectedRecords.some((r) => r.kind === "activity")) return null;
    return { kind: "retention", title: fix.title, confidence: "needs-you", declared: true };
  }
  if (fix.kind === "find-household") {
    const households = issue.affectedRecords.filter((r) => r.kind === "household");
    if (households.length !== 1 || households[0]!.record_gone) return null;
    return { kind: "find-household", title: fix.title, household: households[0]!, confidence: fix.confidence, declared: true };
  }
  if (fix.kind === "same-household") {
    const households = issue.affectedRecords.filter((r) => r.kind === "household" && !r.record_gone);
    if (households.length !== 2) return null;
    return {
      kind: "same-household",
      title: fix.title,
      ...(fix.variant ? { variant: fix.variant } : {}),
      households,
      survivorId: households.some((h) => h.id === fix.survivorId) ? fix.survivorId : null,
      likelySame: fix.likelySame,
      confidence: fix.confidence,
      declared: true,
    };
  }
  if (fix.kind === "choose-answer") {
    // Every answer acts on the issue's own record; once it has been
    // merged away or archived there is nothing left to answer about.
    if (issue.affectedRecords.some((r) => (r.kind === "contact" || r.kind === "household") && r.record_gone)) return null;
    // "Find the household" opens the merge search for the issue's ONE
    // household; without exactly one there is nothing to search from.
    if (
      fix.answers.some((a) => a.verb === "find-household") &&
      issue.affectedRecords.filter((r) => r.kind === "household").length !== 1
    ) {
      return null;
    }
    const answers = [...fix.answers].sort((x, y) => Number(y.recommended === true) - Number(x.recommended === true));
    return { kind: "choose-answer", title: fix.title, answers, confidence: fix.confidence, declared: true };
  }
  if (fix.kind === "same-person") {
    const people = issue.affectedRecords.filter((r) => r.kind === "contact" && !r.record_gone);
    if (people.length < 2) return null;
    return {
      kind: "same-person",
      title: fix.title,
      people,
      matchType: fix.matchType,
      matchValue: fix.matchValue,
      likelySame: fix.likelySame,
      confidence: fix.confidence,
      declared: true,
    };
  }
  if (fix.kind === "fill-one") {
    return {
      kind: "fill-one",
      title: fix.title,
      input: fix.input,
      applyLabel: fix.applyLabel,
      blankApplyLabel: fix.blankApplyLabel ?? null,
      escapes: fix.escapes,
      householdId: fix.householdId,
      verb: fix.verb ?? "verify",
      confidence: fix.confidence,
      declared: true,
    };
  }
  if (fix.kind === "add-policy") {
    return {
      kind: "add-policy",
      title: fix.title,
      prefill: fix.prefill,
      searchHint: fix.searchHint,
      confidence: fix.confidence,
      declared: true,
    };
  }
  if (fix.kind === "pick-one") {
    return {
      kind: "pick-one",
      title: fix.title,
      options: fix.options,
      link: fix.link ?? null,
      confidence: fix.confidence,
      declared: true,
    };
  }
  if (fix.kind === "keep-one") {
    // A record that has since been archived or merged away is not a
    // candidate to keep OR to retire — drop it rather than offer a row
    // the click would fail on.
    const candidates = fix.candidates
      .map((c) => {
        const ref = refById(issue, c.id);
        if (ref?.record_gone) return null;
        return {
          ...(ref ?? { id: c.id, label: "this record" }),
          kind: c.kind,
        } as IssueRef & { kind: MovableKind };
      })
      .filter((c): c is IssueRef & { kind: MovableKind } => c !== null);
    if (candidates.length < 2) return null;
    const keeperId =
      fix.keeperId && candidates.some((c) => c.id === fix.keeperId) ? fix.keeperId : null;
    return {
      kind: "keep-one",
      title: fix.title,
      candidates,
      keeperId,
      ...(fix.keepAll ? { keepAll: fix.keepAll } : {}),
      retire: fix.retire ?? "archive",
      confidence: keeperId ? fix.confidence : "needs-you",
      declared: true,
    };
  }
  if (fix.kind === "type-it-in") {
    const ref = refById(issue, fix.record.id);
    // The record having been archived or merged away since the sweep
    // means there is nothing left to type into. Fall back to the panel.
    if (ref?.record_gone) return null;
    if (fix.editor === "fields") {
      return {
        kind: "type-it-in-fields",
        title: fix.title,
        record: ref ?? { kind: fix.record.kind, id: fix.record.id, label: "this record" },
        householdId: fix.householdId,
        confidence: fix.confidence,
        writer: fix.writer,
        writerId: fix.writerId,
        fields: fix.fields,
        alternatives: fix.alternatives ?? [],
        closeOnSave: fix.closeOnSave === true,
        declared: true,
      };
    }
    return {
      kind: "type-it-in",
      title: fix.title,
      editor: fix.editor,
      record: {
        ...(ref ?? { id: fix.record.id, label: "this record" }),
        kind: fix.record.kind,
      },
      householdId: fix.householdId,
      confidence: fix.confidence,
      declared: true,
    };
  }
  const recordRef = refById(issue, fix.record.id);
  const record: IssueRef & { kind: MovableKind } = {
    ...(recordRef ?? { id: fix.record.id, label: "this record" }),
    kind: fix.record.kind,
  };
  const households = householdRefs(issue);
  const from = refById(issue, fix.fromHouseholdId) ?? households[0] ?? null;
  const toRef = refById(issue, fix.toHouseholdId);
  const to =
    toRef ??
    (fix.toHouseholdId
      ? { kind: "household", id: fix.toHouseholdId, label: "the suggested household" }
      : null);
  // A destination that has since been archived or merged away is not a
  // destination (Baker's twin picker, 2026-07-31), and neither is the
  // household the record is already on: fall back to search.
  const liveTo = to && (to.record_gone || to.id === from?.id) ? null : to;
  return {
    kind: "move-record",
    title: fix.title,
    record,
    from,
    to: liveTo,
    confidence: liveTo ? fix.confidence : "needs-you",
    declared: true,
  };
}

function inferMove(issue: FixIssueLike): ResolvedMove | null {
  const slug = issue.detectorSlug ?? "";
  const verb = issue.recommendation?.verb ?? "";
  const movable = issue.affectedRecords.filter((r) => isMovableKind(r.kind));
  if (movable.length !== 1) return null;
  // A detector that recommends some OTHER change (a merge, an archive) is
  // asking for that, not for a move — mortgage-loan-wrong-household's
  // duplicate-household branch recommends a merge and still names a loan.
  if (verb && verb !== "move-household") return null;
  const isPlacement = verb === "move-household" || PLACEMENT_SLUGS.includes(slug);
  if (!isPlacement) return null;

  const record = movable[0] as IssueRef & { kind: MovableKind };
  const households = householdRefs(issue);
  const from = households[0] ?? null;
  const params = issue.recommendation?.params ?? null;
  const paramTarget =
    params && typeof params.target_household_id === "string"
      ? params.target_household_id
      : null;
  const second = households.find((h) => h.id !== from?.id) ?? null;
  // A recommendation may name a destination the affected records do not
  // list (older detectors did); keep the id so the picker can still pin it.
  const paramRef =
    refById(issue, paramTarget) ??
    (paramTarget ? { kind: "household", id: paramTarget, label: "the suggested household" } : null);
  // Never suggest the household the record is already on.
  const candidate = (paramRef && paramRef.id !== from?.id ? paramRef : null) ?? second;
  const to = candidate && candidate.record_gone ? null : candidate;

  const confidence: CleanupFixConfidence = !to
    ? "needs-you"
    : verb === "move-household" && (issue.confidence ?? 0) >= CONFIDENT_FLOOR
      ? "confident"
      : "check-first";

  return {
    kind: "move-record",
    title: PLACEMENT_TITLES[slug] ?? "Record on the wrong household",
    record,
    from,
    to,
    confidence,
    declared: false,
  };
}

/** The fix this issue offers, or null when the card has nothing to offer
 *  beyond the dispositions — in which case the pane must say so. */
export function resolveFix(issue: FixIssueLike): ResolvedFix | null {
  const declared = issue.fix ? parseCleanupFix(issue.fix) : null;
  if (declared) return fromDeclared(issue, declared);
  return inferMove(issue);
}

/** The pill the person reads instead of "70% · risk 40 · STANDARD". */
export function confidencePill(confidence: CleanupFixConfidence): {
  label: string;
  tone: "sure" | "check" | "info";
} {
  switch (confidence) {
    case "confident":
      return { label: "Confident", tone: "sure" };
    case "check-first":
      return { label: "Check first", tone: "check" };
    default:
      return { label: "Needs you", tone: "info" };
  }
}

/** Plain noun for a movable record kind, for copy like "this loan goes with it". */
export function movableNoun(kind: MovableKind): string {
  switch (kind) {
    case "service_record":
      return "record";
    case "opportunity":
      return "opportunity";
    case "transaction":
      return "processing record";
    case "rhythm_instance":
      return "care plan";
  }
}
